// Top-bar entity switcher (HR) — Company → Group → Entity.
// A custom dropdown (not a portaled MUI Menu) so it positions correctly under
// the app's `body { zoom: 0.8 }`, mirroring the notification bell. Glyph colours
// come from the brand-aware PIE_COLORS palette so it re-themes with Augment/DIH.
import { useState, useRef, useEffect } from 'react';
import { Building2, Check, ChevronDown, Layers } from 'lucide-react';
import { AG, PIE_COLORS } from './theme/tokens';

// Deterministic per-entity colour from the active brand palette.
function glyphColor(key = '') {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  const pal = PIE_COLORS.length ? PIE_COLORS : [AG.primary];
  return pal[h % pal.length];
}
function glyphText(name = '', code = '') {
  const src = String(name || code || '').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase() || '—';
}

// Square tile for an entity: its logo if set, else a monogram on a deterministic
// brand-palette colour. Reused by the switcher and the Organization admin cards.
export function EntityGlyph({ entity, size = 30 }) {
  const radius = Math.round(size * 0.28);
  if (entity?.logoUrl) {
    return (
      <img
        src={entity.logoUrl}
        alt=""
        style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0, display: 'block' }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: glyphColor(entity?.code || entity?.id),
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: Math.round(size * 0.4),
        flexShrink: 0,
        lineHeight: 1,
      }}
    >
      {glyphText(entity?.name, entity?.code)}
    </div>
  );
}

export function EntitySwitcher({ orgTree = [], value = 'all', onChange }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const company = orgTree[0];
  const groups = (company && company.groups) || [];
  const allEntities = groups.flatMap((g) => g.entities || []);
  const current = allEntities.find((e) => e.id === value);
  const isAll = value === 'all' || !current;

  const pick = (id) => { onChange?.(id); setOpen(false); };

  const rowStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '8px 10px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    background: active ? AG.primaryLightest : 'transparent',
    transition: 'background .12s',
  });

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={isAll ? 'All entities' : current.name}
        aria-label={`Active entity: ${isAll ? 'All entities' : current.name}`}
        className="navlift"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          padding: '0 10px',
          borderRadius: 10,
          border: `1px solid ${AG.n200}`,
          background: AG.paper,
          color: AG.text,
          cursor: 'pointer',
          maxWidth: 220,
          fontFamily: 'inherit',
        }}
      >
        {isAll ? (
          <div style={{ width: 24, height: 24, borderRadius: 7, background: AG.n100, color: AG.n500, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Layers size={14} />
          </div>
        ) : (
          <EntityGlyph entity={current} size={24} />
        )}
        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {isAll ? 'All entities' : current.name}
        </span>
        <ChevronDown size={14} style={{ color: AG.n500, flexShrink: 0 }} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 300,
            maxHeight: 440,
            overflowY: 'auto',
            background: AG.paper,
            border: `1px solid ${AG.n200}`,
            borderRadius: 14,
            boxShadow: '0 18px 38px rgba(16,24,40,0.16)',
            zIndex: 1300,
            padding: 6,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: AG.n400, padding: '6px 10px 4px' }}>
            Switch entity
          </div>
          <button
            type="button"
            onClick={() => pick('all')}
            onMouseEnter={() => setHover('all')}
            onMouseLeave={() => setHover(null)}
            style={{ ...rowStyle(isAll), background: isAll ? AG.primaryLightest : hover === 'all' ? AG.n50 : 'transparent' }}
          >
            <div style={{ width: 30, height: 30, borderRadius: 8, background: AG.n100, color: AG.n500, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Layers size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: AG.text }}>Consolidated view</div>
              <div style={{ fontSize: 12, color: AG.n500 }}>All entities</div>
            </div>
            {isAll && <Check size={15} style={{ color: AG.primary, flexShrink: 0 }} />}
          </button>

          {groups.map((g) => (
            <div key={g.id}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: AG.n400, padding: '10px 10px 4px' }}>
                {g.name}
              </div>
              {(g.entities || []).map((e) => {
                const sel = value === e.id;
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => pick(e.id)}
                    onMouseEnter={() => setHover(e.id)}
                    onMouseLeave={() => setHover(null)}
                    style={{ ...rowStyle(sel), background: sel ? AG.primaryLightest : hover === e.id ? AG.n50 : 'transparent' }}
                  >
                    <EntityGlyph entity={e} size={30} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: AG.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {e.name}
                      </div>
                      <div style={{ fontSize: 12, color: AG.n500 }}>
                        {e.type}{e.status && e.status !== 'Active' ? ` · ${e.status}` : ''}
                      </div>
                    </div>
                    {sel && <Check size={15} style={{ color: AG.primary, flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          ))}

          {allEntities.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 10px', fontSize: 13, color: AG.n500 }}>
              <Building2 size={15} /> No entities yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EntitySwitcher;
